import { BuilderMenu, IActionType, TypographyGroupValue } from '@makyoapp/makyo-common'
import { ColorVariant, HoverEffectVariant, RawSpacingInput, TypographyValue } from '@makyoapp/makyo-tw-ui'

import { CoreComponentAction, CoreComponentInfos } from '@/core/models/app-components/types'
import { useCompBackgroundColor } from '@/context/__project__/CompContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { ComponentSettingKey } from '@/shared/builder'
import { getSafely } from '@/shared/safely'

import { CompVerticalMenuProps } from './comp'

export interface CompVerticalMenuWrapperProps {
  menus: BuilderMenu[]
  infos: CoreComponentInfos
  action: CoreComponentAction
  children: (props: CompVerticalMenuProps) => React.ReactNode
}

export function CompVerticalMenuWrapperCore({ menus, infos: { settings }, children }: CompVerticalMenuWrapperProps) {
  const selectedMenuId = settings[ComponentSettingKey.VerticalMenuDataSource]
  const { pages } = useAppPagesContext()

  let menuItems: BuilderMenu | undefined

  if (selectedMenuId === '[screen]') {
    const screens = pages.filter((p) => !p.tableId && p.visible && p.enabled)

    const screenMenuItems: any[] = screens.map((page) => ({
      id: page.id,
      title: page.title,
      icon: page.icon ? { value: page.icon, flag: true } : undefined,
      badgeSize: 's' as const,
      badgeMetadata: page.badgeMetadata,
      actions: [
        {
          id: `${page.id}-goto-action`,
          type: IActionType.GoToScreen,
          settings: {
            screen: page.id,
          },
        },
      ],
      visibility: {
        isAny: true,
        conditions: [],
      },
    }))

    menuItems = {
      id: '[screen]',
      title: 'Screens',
      items: screenMenuItems,
      visibility: {
        isAny: true,
        conditions: [],
      },
    }
  } else {
    menuItems = menus?.find((menu) => menu.id === selectedMenuId)
  }

  // ========================= typography =========================
  const typoSettings = settings[ComponentSettingKey.VerticalMenuDesignTypography] as TypographyGroupValue | undefined
  const titleTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.VerticalMenuDesignTitleTypography],
    {} as TypographyValue
  )
  const itemsTypo = getSafely(
    typoSettings,
    [ComponentSettingKey.VerticalMenuDesignItemsTypography],
    {} as TypographyValue
  )

  // ========================= spacing =========================
  const titleMargin = settings[ComponentSettingKey.VerticalMenuDesignTitleMargin] as RawSpacingInput
  const itemMargin = settings[ComponentSettingKey.VerticalMenuDesignItemsMargin] as RawSpacingInput

  // ========================= alignment =========================
  const alignment = settings[ComponentSettingKey.VerticalMenuDesignAlignment]
  const itemsAlignment = settings[ComponentSettingKey.VerticalMenuDesignItemsAlignment]

  // ========================= color =========================
  const titleColor = settings[ComponentSettingKey.VerticalMenuDesignTitleColor] || 'adaptive'
  const itemsColor = settings[ComponentSettingKey.VerticalMenuDesignItemsColor] || 'adaptive'
  const titleColorSolid = settings[ComponentSettingKey.VerticalMenuDesignTitleColorSolid] as ColorVariant
  const itemsColorSolid = settings[ComponentSettingKey.VerticalMenuDesignItemsColorSolid] as ColorVariant
  const titleColorStartGradient = settings[
    ComponentSettingKey.VerticalMenuDesignTitleColorStartGradient
  ] as ColorVariant
  const itemsColorStartGradient = settings[
    ComponentSettingKey.VerticalMenuDesignItemsColorStartGradient
  ] as ColorVariant
  const titleColorEndGradient = settings[ComponentSettingKey.VerticalMenuDesignTitleColorEndGradient] as ColorVariant
  const itemsColorEndGradient = settings[ComponentSettingKey.VerticalMenuDesignItemsColorEndGradient] as ColorVariant
  const titleColorGradientDirection = settings[
    ComponentSettingKey.VerticalMenuDesignTitleColorGradientDirection
  ] as ColorVariant
  const itemsColorGradientDirection = settings[
    ComponentSettingKey.VerticalMenuDesignItemsColorGradientDirection
  ] as ColorVariant

  const showIcon = settings[ComponentSettingKey.VerticalMenuBehaviorShowIcon]
  const margin = settings[ComponentSettingKey.VerticalMenuDesignCompMargin] as RawSpacingInput
  const showTitle = settings[ComponentSettingKey.VerticalMenuBehaviorShowTitle]

  const hoverEffect = settings[ComponentSettingKey.VerticalMenuDesignHoverEffect] as HoverEffectVariant

  const orientation = settings[ComponentSettingKey.VerticalMenuDesignOrientation] || 'vertical'

  const { currentParentBg } = useCompBackgroundColor()

  // ========================= overflow menu =========================
  const maxVisibleItems = settings[ComponentSettingKey.VerticalMenuDataMaxVisibleItems] as number | undefined
  const overflowIcon = settings[ComponentSettingKey.VerticalMenuDataOverflowIcon] as string | undefined
  const overflowMenuBackgroundColor = settings[ComponentSettingKey.VerticalMenuDesignOverflowMenuBackgroundColor] as
    | ColorVariant
    | undefined
  const overflowMenuDropdownAlign = settings[ComponentSettingKey.VerticalMenuDesignOverflowMenuDropdownAlign] as
    | 'start'
    | 'center'
    | 'end'
    | undefined

  const overflowMenuProps =
    maxVisibleItems !== undefined || overflowIcon || overflowMenuBackgroundColor || overflowMenuDropdownAlign
      ? {
          backgroundColor: overflowMenuBackgroundColor,
          dropdownAlign: overflowMenuDropdownAlign,
        }
      : undefined

  return children({
    menuItems: menuItems?.items || [],
    spacing: 0,
    title: menuItems?.title || '',
    backgroundColor: currentParentBg || '',
    titleTypo: titleTypo,
    itemsTypo: itemsTypo,
    titleMargin: titleMargin,
    itemMargin: itemMargin,
    orientation,
    alignment,
    menuAlignment: itemsAlignment,
    titleColor: titleColor,
    itemsColor: itemsColor,
    titleColorSolid: titleColorSolid,
    itemsColorSolid: itemsColorSolid,
    titleColorStartGradient: titleColorStartGradient,
    itemsColorStartGradient: itemsColorStartGradient,
    titleColorEndGradient: titleColorEndGradient,
    titleColorGradientDirection: titleColorGradientDirection,
    itemsColorEndGradient: itemsColorEndGradient,
    itemsColorGradientDirection: itemsColorGradientDirection,
    showIcon: showIcon,
    margin: margin,
    hoverEffect: hoverEffect,
    hoverIntensity: 'md',
    showTitle,
    maxVisibleItems,
    overflowIcon,
    overflowMenuProps,
  })
}
